import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Search, ChevronDown, X } from 'lucide-react';
import { getChampionIconUrl } from '../lib/champions';

export default function ChampionSelect({ value, onChange, champions, version, label, accent = 'cyan', placeholder = 'Rechercher...', sidelined = [] }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 10);
    }
  }, [open]);

  const filtered = useMemo(() => {
    if (!champions) return [];
    if (!query) return champions;
    const q = query.toLowerCase();
    return champions.filter(c => c.name.toLowerCase().includes(q) || c.id.toLowerCase().includes(q));
  }, [champions, query]);

  const selected = champions?.find(c => c.name === value || c.id === value);
  const borderColor = accent === 'cyan' ? 'focus-within:border-cyan-400' : accent === 'magenta' ? 'focus-within:border-fuchsia-400' : 'focus-within:border-amber-400';
  const ringColor = accent === 'cyan' ? 'border-cyan-400/40' : accent === 'magenta' ? 'border-fuchsia-400/40' : 'border-amber-400/40';

  return (
    <div className="relative" ref={ref}>
      {label && <div className="text-[10px] tracking-[0.25em] text-zinc-500 uppercase mb-2 font-semibold">{label}</div>}
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`w-full bg-zinc-900 border border-zinc-800 text-white p-2.5 font-mono text-sm flex items-center gap-2 ${borderColor} transition hover:border-zinc-700`}
      >
        {selected && (
          <img
            src={getChampionIconUrl(selected.id, version)}
            alt=""
            className={`w-6 h-6 border ${ringColor} object-cover`}
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        )}
        <span className="flex-1 text-left">{selected?.name || 'Sélectionner...'}</span>
        <ChevronDown size={14} className={`text-zinc-500 transition ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-zinc-950 border border-zinc-700 z-50 shadow-2xl shadow-black">
          <div className="p-2 border-b border-zinc-800 flex items-center gap-2">
            <Search size={14} className="text-zinc-500" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={placeholder}
              className="flex-1 bg-transparent text-white text-sm outline-none"
            />
            {query && (
              <button onClick={() => setQuery('')} className="text-zinc-600 hover:text-white">
                <X size={14} />
              </button>
            )}
          </div>
          <div className="max-h-64 overflow-y-auto">
            {filtered.length === 0 && (
              <div className="p-4 text-center text-zinc-500 text-sm font-mono">Aucun résultat</div>
            )}
            {filtered.map(c => {
              // V6.8 : marquer les champions sidelined visuellement (mais sélectionnables)
              const isSidelined = sidelined.some(s => s && s.toLowerCase() === c.name.toLowerCase());
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => {
                    onChange(c.name);
                    setOpen(false);
                    setQuery('');
                  }}
                  className={`w-full flex items-center gap-3 p-2 hover:bg-zinc-800 transition text-left ${selected?.id === c.id ? 'bg-zinc-800/50' : ''} ${isSidelined ? 'opacity-50' : ''}`}
                >
                  <img
                    src={getChampionIconUrl(c.id, version)}
                    alt=""
                    className={`w-8 h-8 object-cover flex-shrink-0 ${isSidelined ? 'grayscale' : ''}`}
                    loading="lazy"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                  <span className={`text-sm font-mono ${isSidelined ? 'line-through text-zinc-500' : 'text-white'}`}>{c.name}</span>
                  {isSidelined && (
                    <span className="ml-auto text-[8px] font-bold tracking-wider text-rose-400 bg-rose-400/10 border border-rose-400/30 px-1.5 py-0.5">
                      MIS DE CÔTÉ
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
